import React from 'react';

import { Box } from '@rbilabs/components-library/build/components/layout';
import { useIntl } from 'react-intl';
import styled, { useTheme } from 'styled-components';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import Picture from 'components/picture';
import useMediaQuery from 'hooks/use-media-query';
import HeroLink from 'pages/home/hero-link';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useStoreContext } from 'state/store';
import { primitive } from 'styles/constants/primitives';
import { EventName, emitEvent } from 'utils/event-hub';
import { routes } from 'utils/routing';

import HeroMaskCards from './hero-mask-cards';
import { MainHeroText } from './main-hero-text';
import {
  BottomHeroButtonWrapper,
  DoubleActionBtnWrapper,
  HeroContainer,
  HeroContent,
  Icon,
  LeftDesktopBtnWrapper,
  WhiteBGWrapper,
} from './styled';
import { IMainHero } from './types';

const StyledPicture = styled(Picture)`
  img {
    object-position: 50% 50%;
  }
`;

const MainHero: React.FC<IMainHero> = ({
  image,
  mobileImage,
  html,
  actionTo,
  heroTextColor,
  heroSuperText,
  heroActionText,
  heroSubText,
  heroMaskCards,
  heroActionLeft,
  heroActionLeftLink,
  heroActionLeftIcon,
  heroActionRight,
  heroActionRightLink,
  heroActionRightIcon,
}) => {
  const { formatMessage } = useIntl();
  const { isStoreOpenAndAvailable } = useStoreContext();
  const menuHref = isStoreOpenAndAvailable ? routes.menu : routes.storeLocator;
  const isMobile = useMediaQuery('mobile');
  const isDesktop = useMediaQuery('desktop');
  const enableSideBySideHeroButtons = useFlag(LaunchDarklyFlag.ENABLE_SIDE_BY_SIDE_HERO_BUTTONS);
  const enableBottomHeroButton = useFlag(LaunchDarklyFlag.ENABLE_BOTTOM_HERO_BUTTON);
  const theme = useTheme();

  // TODO: Refactor these so that we're not creating components inside of components...
  const DefaultButton = () => (
    <Box
      position={isDesktop ? 'static' : 'relative'}
      margin={isDesktop ? `${primitive.$spacing9} 0 0` : '0'}
      top={isDesktop ? '0' : `-${primitive.$spacing6}`}
      justify={isDesktop ? 'flex-start' : 'center'}
    >
      <HeroLink to={actionTo || menuHref}>
        <ActionButton
          variant={isDesktop ? ActionButtonVariants.INVERSE : ActionButtonVariants.PRIMARY}
          data-testid="default-action-button"
        >
          {heroActionText ? heroActionText : formatMessage({ id: 'viewMenu' })}
        </ActionButton>
      </HeroLink>
    </Box>
  );

  // Place action in dom depending on mobile/desktop
  // Show default or side by side buttons depending on sanity
  const ActionMobile = () =>
    enableSideBySideHeroButtons ? (
      <DoubleActionBtnWrapper>
        <WhiteBGWrapper>
          <Box width="100%" margin="0 2px">
            <ActionButton onlyIcon isSquare fullWidth wrapText to={heroActionLeftLink || menuHref}>
              {heroActionLeftIcon && <Icon image={heroActionLeftIcon} alt="" aria-hidden="true" />}
              <span>{heroActionLeft}</span>
            </ActionButton>
          </Box>
          <Box width="100%" margin="0 2px">
            <ActionButton onlyIcon isSquare fullWidth wrapText to={heroActionRightLink || menuHref}>
              {heroActionRightIcon && (
                <Icon image={heroActionRightIcon} alt="" aria-hidden="true" />
              )}
              <span>{heroActionRight}</span>
            </ActionButton>
          </Box>
        </WhiteBGWrapper>
      </DoubleActionBtnWrapper>
    ) : (
      <DefaultButton />
    );

  const ActionDesktop = () =>
    enableSideBySideHeroButtons ? (
      <DoubleActionBtnWrapper>
        <ActionButton variant={ActionButtonVariants.INVERSE} to={heroActionLeftLink || menuHref}>
          {heroActionLeftIcon && <Icon image={heroActionLeftIcon} alt="" aria-hidden="true" />}
          <span>{heroActionLeft}</span>
        </ActionButton>
        <LeftDesktopBtnWrapper>
          <ActionButton variant={ActionButtonVariants.INVERSE} to={heroActionRightLink || menuHref}>
            {heroActionRightIcon && <Icon image={heroActionRightIcon} alt="" aria-hidden="true" />}
            <span>{heroActionRight}</span>
          </ActionButton>
        </LeftDesktopBtnWrapper>
      </DoubleActionBtnWrapper>
    ) : (
      <DefaultButton />
    );

  return (
    <>
      <StyledPicture
        image={isMobile && mobileImage ? mobileImage : image}
        alt=""
        onImageLoaded={() => {
          emitEvent(EventName.MAIN_HERO_IMAGE_LOADED);
        }}
      >
        <HeroContent>
          <HeroContainer>
            <MainHeroText
              heroSuperText={heroSuperText}
              defaultTextColor={theme.token('text-reversed')}
              heroTextColor={heroTextColor}
              html={html}
              heroSubText={heroSubText}
            />
            {isDesktop && !enableBottomHeroButton && <ActionDesktop />}
          </HeroContainer>
        </HeroContent>
      </StyledPicture>
      {isMobile && !enableBottomHeroButton && <ActionMobile />}

      {enableBottomHeroButton && (
        <BottomHeroButtonWrapper data-testid="bottom-hero-button">
          <HeroLink to={actionTo || menuHref}>
            <ActionButton variant={ActionButtonVariants.PRIMARY}>
              {heroActionText ? heroActionText : formatMessage({ id: 'viewMenu' })}
            </ActionButton>
          </HeroLink>
        </BottomHeroButtonWrapper>
      )}

      {heroMaskCards && heroMaskCards.length > 0 && <HeroMaskCards cards={heroMaskCards} />}
    </>
  );
};

export default MainHero;
